import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { useAuthStore } from "@/modules/auth";
import type { HolidaySetRef } from "./holidays";

export type PayScheme = "hourly" | "fixed_days";

/** "" = global percent fallback; percent = % of MTD accrual; amount = absolute cap. */
export type AdvanceBasis = "" | "percent" | "amount";

export interface EmployeeRow {
  id: string;
  userId: string;
  empId: string;
  fullName: string;
  email: string;
  role: string;
  payScheme: PayScheme;
  committedDays: number | null;
  overtimeEligible: boolean;
  advanceBasis: AdvanceBasis;
  advanceCapValue: string | null;
  holidaySets: HolidaySetRef[];
  joinDate: string;
  exitDate: string | null;
  pan: string;
  dob: string | null;
  address: string;
  bankAccount: string;
  ifsc: string;
  bankName: string;
  currentBaseSalary: string | null;
  primaryLocation: {
    id: string;
    name: string;
    shiftFrom: string | null;
    shiftTo: string | null;
  } | null;
  status: "active" | "inactive";
  createdAt: string | null;
  updatedAt: string | null;
}

export interface EmployeeListResponse {
  results: EmployeeRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface EmployeeCreateInput {
  userId: string;
  baseSalary: number;
  payScheme: PayScheme;
  committedDays?: number | null;
  overtimeEligible: boolean;
  advanceBasis?: AdvanceBasis;
  advanceCapValue?: number | null;
  holidaySetIds: string[];
  joinDate?: string | null;
  exitDate?: string | null;
  pan?: string;
  dob?: string | null;
  address?: string;
  bankAccount?: string;
  ifsc?: string;
  bankName?: string;
}

export type EmployeeUpdateInput = Partial<Omit<EmployeeCreateInput, "userId" | "baseSalary">>;

export interface SalaryRevisionDoc {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  docNo: string | null;
}

export interface SalaryRevisionRow {
  id: string;
  amount: string;
  effectiveFrom: string;
  createdAt: string | null;
  document: SalaryRevisionDoc | null;
}

export interface PayrollSettingRow {
  key: string;
  value: unknown;
  editable: boolean;
}

export interface PayrollSettingsResponse {
  settings: PayrollSettingRow[];
}

export function usePayrollEmployees(
  params: {
    status?: "active" | "inactive" | "all" | "";
    search?: string;
    payScheme?: PayScheme | "";
  } = {},
) {
  return useQuery<EmployeeListResponse>({
    queryKey: ["payrollEmployees", "list", params],
    queryFn: () =>
      api
        .get<EmployeeListResponse>("/payroll/employees", {
          params: {
            status: params.status || undefined,
            search: params.search || undefined,
            payScheme: params.payScheme || undefined,
          },
        })
        .then((r) => r.data),
  });
}

export function usePayrollEmployee(id: string | undefined) {
  return useQuery<EmployeeRow>({
    queryKey: ["payrollEmployees", "detail", id],
    queryFn: () => api.get<EmployeeRow>(`/payroll/employees/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeCreateInput) =>
      api.post<EmployeeRow>("/payroll/employees", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useUpdatePayrollEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeUpdateInput) =>
      api.patch<EmployeeRow>(`/payroll/employees/${id}`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useDeactivatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmployeeRow>(`/payroll/employees/${id}/deactivate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useActivatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmployeeRow>(`/payroll/employees/${id}/activate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useSalaryRevisions(employeeId: string | undefined) {
  return useQuery<SalaryRevisionRow[]>({
    queryKey: ["payrollEmployees", employeeId ?? "_", "revisions"],
    queryFn: () =>
      api
        .get<SalaryRevisionRow[]>(`/payroll/employees/${employeeId}/revisions`)
        .then((r) => r.data),
    enabled: !!employeeId,
  });
}

export function useReviseSalary(employeeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount: number; effectiveFrom: string; attachmentId?: string }) =>
      api
        .post<SalaryRevisionRow>(`/payroll/employees/${employeeId}/revisions`, input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useSetRevisionDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { revisionId: string; attachmentId: string }) =>
      api
        .post<SalaryRevisionRow>(`/payroll/revisions/${input.revisionId}/document`, {
          attachmentId: input.attachmentId,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

/* -------------------------------- runs -------------------------------- */

export type RunStatus = "draft" | "review" | "finalized";

export interface RunIssue {
  employeeId: string;
  name: string;
  reason: string;
}

export interface PayrollRunRow {
  id: string;
  month: string; // ISO date, first of month
  status: RunStatus;
  computedAt: string | null;
  submittedAt: string | null;
  finalizedAt: string | null;
  reopenedAt: string | null;
  reopenReason: string;
  issues: RunIssue[];
  lineCount: number;
  totalGross: string;
  totalNet: string;
  disbursedCount: number;
}

export interface PayrollLineRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  payScheme: PayScheme;
  baseSalary: string;
  totalFractions: string;
  targetUnits: string;
  otUnits: string;
  workedHours: string;
  requiredHours: string;
  otHours: string;
  hourlyRate: string;
  sundayCredits: number;
  holidayCredits: number;
  presentDayUnits: string;
  /** Working-day status counts (hourly scheme only; null for fixed_days). */
  dayCounts: {
    fullPunchDays: number;
    noMarkoutDays: number;
    leaveDays: number;
    absentDays: number;
  } | null;
  /** Pay components expressed as equivalent hours; totalHours × ratePerHour =
   *  basic earned. Hourly scheme with a known rate only; null otherwise. */
  hoursBreakup: {
    ratePerHour: string;
    components: {
      key: "regular" | "overtime" | "halfDay" | "sunday" | "holiday";
      label: string;
      hours: string;
      amount: string;
    }[];
    totalHours: string;
    totalAmount: string;
  } | null;
  basePay: string;
  otPay: string;
  sundayPay: string;
  holidayPay: string;
  voucherTotal: string;
  advanceDeduction: string;
  reimbursementTotal: string;
  gross: string;
  net: string;
  paidAmount: string;
  remainingAmount: string;
  fullyPaid: boolean;
}

export interface CalcSnapshotDay {
  date: string;
  isSunday: boolean;
  isHoliday: boolean;
  leave: string | null;
  expectedHours: string;
  workedHours: string;
  fraction: string;
  targetUnits: string;
  note: string;
  /** Punch times as "HH:MM" local, or null (no punch / legacy snapshot). */
  inTime: string | null;
  outTime: string | null;
}

export interface CalcSnapshot {
  payScheme: PayScheme;
  daysInMonth: number;
  totalFractions: string;
  targetUnits: string;
  otUnits: string;
  workedHours: string;
  requiredHours: string;
  otHours: string;
  hourlyRate: string;
  dailyWage: string;
  shiftHours: string;
  numWorkingDays: number;
  standardMonthlyHours: string;
  sundayCredits: number;
  holidayCredits: number;
  creditedSundays: string[];
  creditedHolidays: string[];
  presentDayUnits: string;
  committedDays: number | null;
  days: CalcSnapshotDay[];
}

export interface PayrollLineBreakdown extends PayrollLineRow {
  calcSnapshot: CalcSnapshot;
}

export interface EarningsEstimate {
  asOf: string;
  month: string;
  earnedSoFar: string;
  payScheme: PayScheme;
  /** HR run-lines display metrics — rate settings (multipliers/ratios) never ship here. */
  components: {
    hourlyRate: string;
    workedHours: string;
    requiredHours: string;
    otHours: string;
    paidSundays: number;
    paidHolidays: number;
    daysWorked: string;
  };
  basic: {
    baseSalary: string | null;
    effectiveFrom: string | null;
    dailyWage: string;
  };
  breakdown?: CalcSnapshot;
}

export function usePayrollRuns() {
  return useQuery<PayrollRunRow[]>({
    queryKey: ["payrollRuns", "list"],
    queryFn: () => api.get<PayrollRunRow[]>("/payroll/runs").then((r) => r.data),
  });
}

export function usePayrollRun(id: string | undefined) {
  return useQuery<PayrollRunRow>({
    queryKey: ["payrollRuns", "detail", id],
    queryFn: () => api.get<PayrollRunRow>(`/payroll/runs/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function usePayrollRunLines(runId: string | undefined) {
  return useQuery<PayrollLineRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "lines"],
    queryFn: () =>
      api.get<PayrollLineRow[]>(`/payroll/runs/${runId}/lines`).then((r) => r.data),
    enabled: !!runId,
  });
}

export function useComputeRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; month: number }) =>
      api.post<PayrollRunRow>("/payroll/runs", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

function runAction(action: string) {
  return (runId: string, body: object = {}) =>
    api.post<PayrollRunRow>(`/payroll/runs/${runId}/${action}`, body).then((r) => r.data);
}

export function useRunAction(action: "submit-review" | "return-to-draft" | "finalize") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => runAction(action)(runId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useReopenRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { runId: string; reason: string }) =>
      runAction("reopen")(input.runId, { reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useLineBreakdown(lineId: string | undefined) {
  return useQuery<PayrollLineBreakdown>({
    queryKey: ["payrollLines", lineId ?? "_", "breakdown"],
    queryFn: () =>
      api.get<PayrollLineBreakdown>(`/payroll/lines/${lineId}/breakdown`).then((r) => r.data),
    enabled: !!lineId,
  });
}

export function useMyEarningsEstimate(enabled = true) {
  return useQuery<EarningsEstimate>({
    queryKey: ["payroll", "myEstimate"],
    queryFn: () => api.get<EarningsEstimate>("/payroll/me/estimate").then((r) => r.data),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/* -------------------------------- slips -------------------------------- */

export type SlipStatus = "valid" | "superseded" | "void";

export interface PayslipRow {
  id: string;
  slipNo: string;
  period: string; // ISO date, first of month
  net: string;
  version: number;
  status: SlipStatus;
  attachmentId: string;
  issuedAt: string | null;
  replacedBy: string | null;
  paidOn: string | null;
  paidAmount: string | null;
  /** Own-breakdown link — set only while the issuing run stays finalized. */
  lineId: string | null;
}

export function useMySlips() {
  return useQuery<PayslipRow[]>({
    queryKey: ["payroll", "mySlips"],
    queryFn: () => api.get<PayslipRow[]>("/payroll/me/slips").then((r) => r.data),
  });
}

export function useRunSlips(runId: string | undefined, enabled = true) {
  return useQuery<(PayslipRow & { employeeName: string; empId: string })[]>({
    queryKey: ["payrollRuns", runId ?? "_", "slips"],
    queryFn: () =>
      api
        .get<(PayslipRow & { employeeName: string; empId: string })[]>(
          `/payroll/runs/${runId}/slips`,
        )
        .then((r) => r.data),
    enabled: enabled && !!runId,
  });
}

/* ------------------------------- advances ------------------------------- */

export type AdvanceStatus =
  | "pending_hr"
  | "pending_final"
  | "active"
  | "closed"
  | "rejected"
  | "cancelled";

export interface AdvanceLedgerEntry {
  id: string;
  kind: "payroll" | "manual";
  amount: string;
  runMonth: string | null;
  reference: string;
  note: string;
  recordedAt: string | null;
  balanceAfter: string;
}

export interface AdvanceRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  amount: string;
  reason: string;
  balance: string;
  requestedInstallments: number;
  installmentsTotal: number;
  monthsRecovered: number;
  monthlyDue: string | null;
  accruedAtRequest: string;
  status: AdvanceStatus;
  hrApprovedAt: string | null;
  finalApprovedAt: string | null;
  settlementRef: string;
  settlementNote: string;
  resolutionNote: string;
  createdAt: string | null;
  ledger?: AdvanceLedgerEntry[];
}

export interface AdvanceEligibility {
  tenureOk: boolean;
  minTenureYears: number;
  basis: "percent" | "amount";
  capValue: string;
  capLabel: string;
  earnedSoFar: string;
  maxAmount: string;
  maxInstallments: number;
  hasOpenAdvance: boolean;
}

export interface MyAdvancesResponse {
  advances: AdvanceRow[];
  eligibility: AdvanceEligibility | null;
}

export function useAdvances(
  params: { status?: string } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<AdvanceRow[]>({
    queryKey: ["advances", "list", params],
    queryFn: () =>
      api
        .get<AdvanceRow[]>("/payroll/advances", {
          params: { status: params.status || undefined },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
  });
}

export function useMyAdvances() {
  return useQuery<MyAdvancesResponse>({
    queryKey: ["advances", "mine"],
    queryFn: () => api.get<MyAdvancesResponse>("/payroll/me/advances").then((r) => r.data),
  });
}

export function useRequestAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount: number; reason: string; installments: number }) =>
      api.post<AdvanceRow>("/payroll/advances", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

export function useAdvanceAction(action: "approve-hr" | "cancel") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (advanceId: string) =>
      api.post<AdvanceRow>(`/payroll/advances/${advanceId}/${action}`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

/** Final approval also captures the disbursal settlement reference (voucher /
 *  txn no.) + optional remark — how the advance cash was paid to the employee. */
export function useFinalApproveAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      advanceId: string;
      settlementRef: string;
      settlementNote?: string;
      installments?: number | null;
    }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/approve-final`, {
          settlementRef: input.settlementRef,
          settlementNote: input.settlementNote ?? "",
          installments: input.installments ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

/** HR records a cash/bank repayment made outside the payroll run. */
export function useRecordAdvanceRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      advanceId: string;
      amount: number;
      reference?: string;
      note?: string;
    }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/repayments`, {
          amount: input.amount,
          reference: input.reference ?? "",
          note: input.note ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

export function useRejectAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { advanceId: string; note: string }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/reject`, { note: input.note })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

/* ----------------------------- reimbursements ----------------------------- */

export type ClaimStatus = "pending" | "approved" | "rejected" | "cancelled" | "paid";
export type StepState = "pending" | "approved" | "rejected" | "skipped" | "overridden";

export interface ClaimStep {
  order: number;
  kind: "requester_dept_head" | "specific_dept_head" | "role";
  departmentName: string | null;
  role: string | null;
  minAmount: string | null;
  approverName: string | null;
  state: StepState;
  skippedReason: string | null;
  actedByName: string | null;
  actedAt: string | null;
  note: string;
}

export interface ClaimRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  categoryLabel: string;
  workUnitId: string;
  workUnitCtrlNo: string;
  amount: string;
  note: string;
  receiptId: string;
  status: ClaimStatus;
  resolutionNote: string;
  currentOrder: number | null;
  steps: ClaimStep[];
  createdAt: string | null;
  canAct?: boolean;
  actIsOverride?: boolean;
}

export interface ClaimCategory {
  id: string;
  label: string;
  status: "active" | "inactive";
}

export interface ChainStepConfig {
  order?: number;
  kind: "requester_dept_head" | "specific_dept_head" | "role";
  departmentId: string | null;
  departmentName?: string | null;
  role: string | null;
  minAmount: string | null;
}

export function useClaims(params: { status?: string } = {}) {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "list", params],
    queryFn: () =>
      api
        .get<ClaimRow[]>("/payroll/reimbursements", {
          params: { status: params.status || undefined },
        })
        .then((r) => r.data),
  });
}

export function useMyClaims() {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "mine"],
    queryFn: () => api.get<ClaimRow[]>("/payroll/me/reimbursements").then((r) => r.data),
  });
}

export function useMyClaimApprovals(enabled = true) {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "approvals"],
    queryFn: () =>
      api.get<ClaimRow[]>("/payroll/me/reimbursement-approvals").then((r) => r.data),
    enabled,
  });
}

export function useSubmitClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      categoryId: string;
      workUnitId: string;
      amount: number;
      note?: string;
      receiptId: string;
    }) => api.post<ClaimRow>("/payroll/reimbursements", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useActOnClaimStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      claimId: string;
      order: number;
      decision: "approve" | "reject";
      note?: string;
    }) =>
      api
        .post<ClaimRow>(
          `/payroll/reimbursements/${input.claimId}/steps/${input.order}/${input.decision}`,
          { note: input.note ?? "" },
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useCancelClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) =>
      api.post<ClaimRow>(`/payroll/reimbursements/${claimId}/cancel`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useClaimCategories(status: "active" | "all" = "active") {
  return useQuery<ClaimCategory[]>({
    queryKey: ["claimCategories", status],
    queryFn: () =>
      api
        .get<ClaimCategory[]>("/payroll/reimbursement-categories", { params: { status } })
        .then((r) => r.data),
  });
}

export function useCreateClaimCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string }) =>
      api.post<ClaimCategory>("/payroll/reimbursement-categories", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claimCategories"] }),
  });
}

export function useSetClaimCategoryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; action: "activate" | "deactivate" }) =>
      api
        .post<ClaimCategory>(
          `/payroll/reimbursement-categories/${input.id}/${input.action}`,
          {},
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claimCategories"] }),
  });
}

export function useApprovalChain() {
  return useQuery<{ steps: ChainStepConfig[]; isDefault: boolean }>({
    queryKey: ["approvalChain"],
    queryFn: () =>
      api
        .get<{ steps: ChainStepConfig[]; isDefault: boolean }>("/payroll/approval-chain")
        .then((r) => r.data),
  });
}

export function useUpdateApprovalChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { steps: ChainStepConfig[] }) =>
      api
        .put<{ steps: ChainStepConfig[]; isDefault: boolean }>("/payroll/approval-chain", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvalChain"] }),
  });
}

/* --------------------------- vouchers + disbursal --------------------------- */

export interface VoucherItemRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  label: string;
  voucherNo: string;
  amount: string;
  note: string;
  attachmentId: string | null;
  createdAt: string | null;
}

export interface DisbursalAllocationRow {
  id: string;
  lineId: string;
  empId: string;
  fullName: string;
  amount: string;
}

export interface DisbursalBatchRow {
  id: string;
  disbursedOn: string;
  mode: "bank" | "cash" | "upi" | "cheque";
  reference: string;
  note: string;
  voucherId: string | null;
  employeeCount: number;
  totalAmount: string;
  allocations: DisbursalAllocationRow[];
  isVoided: boolean;
  voidReason: string;
  voidedAt: string | null;
  voidedBy: string | null;
  createdAt: string | null;
}

export interface DisbursalAllocationInput {
  lineId: string;
  amount: number;
}

export function useRunVouchers(runId: string | undefined) {
  return useQuery<VoucherItemRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "vouchers"],
    queryFn: () =>
      api.get<VoucherItemRow[]>(`/payroll/runs/${runId}/vouchers`).then((r) => r.data),
    enabled: !!runId,
  });
}

export function useAddVoucherItem(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      employeeId: string;
      label: string;
      voucherNo?: string;
      amount: number;
      note?: string;
      attachmentId?: string | null;
    }) =>
      api.post<VoucherItemRow>(`/payroll/runs/${runId}/vouchers`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useRemoveVoucherItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voucherId: string) =>
      api.delete(`/payroll/vouchers/${voucherId}`).then(() => true),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useRunDisbursalBatches(runId: string | undefined) {
  return useQuery<DisbursalBatchRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "disbursalBatches"],
    queryFn: () =>
      api
        .get<DisbursalBatchRow[]>(`/payroll/runs/${runId}/disbursal-batches`)
        .then((r) => r.data),
    enabled: !!runId,
  });
}

export function useCreateDisbursalBatch(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      allocations: DisbursalAllocationInput[];
      disbursedOn: string;
      mode: DisbursalBatchRow["mode"];
      reference?: string;
      note?: string;
      voucherId?: string | null;
    }) =>
      api
        .post<DisbursalBatchRow>(`/payroll/runs/${runId}/disbursal-batches`, input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useVoidDisbursalBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { batchId: string; reason: string }) =>
      api
        .post<DisbursalBatchRow>(`/payroll/disbursal-batches/${input.batchId}/void`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function slipPreviewUrl(): string {
  const base = api.defaults.baseURL ?? "";
  const token = useAuthStore.getState().accessToken;
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${base}/payroll/slip-preview${qs}`;
}

export function usePayrollSettings() {
  return useQuery<PayrollSettingsResponse>({
    queryKey: ["payrollSettings"],
    queryFn: () => api.get<PayrollSettingsResponse>("/payroll/settings").then((r) => r.data),
  });
}

export function useUpdatePayrollSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      api.patch<PayrollSettingsResponse>("/payroll/settings", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollSettings"] }),
  });
}
